import { ApolloClient, InMemoryCache, createHttpLink, split, from, NormalizedCacheObject } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { createClient } from 'graphql-ws';
import { getAuth } from 'firebase/auth';
import { NavPages, NavUtils } from 'utils';

const hasuraDomain = process.env.REACT_APP_HASURA_DOMAIN;
const hasuraPass = process.env.REACT_APP_HASURA_ADMIN_SECRET;
const hasuraSsl = process.env.REACT_APP_HASURA_SSL !== 'false' ? 's' : '';

export function getApolloClient(): ApolloClient<NormalizedCacheObject> {
    const loginPath = NavUtils.generateNavPath(NavPages.Login);
    if((!hasuraPass || hasuraPass.length === 0) && !getAuth().currentUser && window.location.pathname !== loginPath) {
        const returnUrl = encodeURI(window.location.pathname);
        window.location.href = `${loginPath}${returnUrl.length > 1 ? `?returnUrl=${returnUrl}` : ''}`;
    }
    
    const wsLink = new GraphQLWsLink(
        createClient({
            url: `ws${hasuraSsl}://${hasuraDomain}/v1/graphql`,
            connectionParams: async() => {
                if (hasuraPass && hasuraPass.length > 0) {
                    return {
                        headers: {
                            'x-hasura-admin-secret': hasuraPass
                        }
                    };
                }

                const currentUser = getAuth().currentUser;
                if(currentUser) {
                    const token = await currentUser.getIdToken(true);
                    return {
                        headers: {
                            authorization: `Bearer ${token}`,
                        }
                    };
                }

                return {};
            }
        })
    );

    const httpLink = createHttpLink({
        uri: `http${hasuraSsl}://${hasuraDomain}/v1/graphql`,
        fetchOptions: {
            keepalive: true
        }
    });

    const authLink = setContext(async (_, { headers }) => {
        if (hasuraPass && hasuraPass.length > 0) {
            return {
                headers: {
                    ...headers,
                    'x-hasura-admin-secret': hasuraPass
                }
            };
        }

        const currentUser = getAuth().currentUser;
        if (currentUser)
            return {
                headers: {
                    ...headers,
                    Authorization: `Bearer ${await currentUser.getIdToken()}`
                }
            };

        return {
            headers: {
                ...headers
            }
        };
    });

    const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors)
            graphQLErrors.forEach(({ message, locations, path }) =>
                console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
            );

        if (networkError) console.log(`[Network error]: ${networkError}`);
    });

    // The split function takes three parameters:
    //
    // * A function that's called for each operation to execute
    // * The Link to use for an operation if the function returns a "truthy" value
    // * The Link to use for an operation if the function returns a "falsy" value
    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        wsLink,
        authLink.concat(httpLink)
    );

    // for local development use http calls for easier debugging
    if (window.location.origin.indexOf('localhost') > -1) {
        return new ApolloClient({
            link: from([errorLink, splitLink]),
            cache: new InMemoryCache()
        });
    }

    // on deployed environments use websocket for all graphQL calls
    return new ApolloClient({
        link: from([errorLink, wsLink]),
        cache: new InMemoryCache()
    });
}
